import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";
import type { LearnFAQ } from "@/pages/learn/learnShared";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question: "¿Qué parte del cerebro estimula SAINT® TMS?",
    answer:
      "SAINT® estimula la corteza prefrontal dorsolateral izquierda (DLPFC), una región implicada en la regulación del ánimo. A diferencia de la TMS convencional, SAINT® usa fMRI para personalizar el sitio exacto de estimulación dentro de la DLPFC para cada paciente.",
  },
  {
    question: "¿Cómo funciona la orientación por fMRI en SAINT®?",
    answer:
      "La fMRI mapea la conectividad funcional entre regiones cerebrales. Un algoritmo propietario identifica la subregión específica de su DLPFC con la conexión más fuerte a los circuitos profundos de regulación del ánimo. Ese es su objetivo personalizado, único para su cerebro.",
  },
  {
    question:
      "¿En qué se diferencia la orientación de SAINT® de la TMS normal?",
    answer:
      "La TMS convencional orienta la DLPFC con medidas del cuero cabelludo (un promedio poblacional). SAINT® usa fMRI para encontrar el objetivo individualizado de cada paciente: la subregión específica más conectada a los circuitos del ánimo. Este enfoque personalizado es una innovación clave de SAINT®.",
  },
];

export const esBrainTarget = makeEsArticle({
  slug: "what-part-of-brain-does-saint-target",
  title: "¿Qué parte del cerebro estimula SAINT® TMS? | Neuro Wellness",
  description:
    "SAINT® TMS estimula la corteza prefrontal dorsolateral izquierda, personalizada para cada paciente con fMRI. Conozca cómo funciona la orientación cerebral en SAINT®.",
  h1: "¿Qué parte del cerebro estimula SAINT® TMS?",
  breadcrumbLabel: "Objetivo cerebral de SAINT®",
  shortAnswer:
    "SAINT® TMS estimula la corteza prefrontal dorsolateral izquierda (DLPFC), una región cerebral implicada en la regulación del ánimo. A diferencia de la TMS convencional, que orienta esta área con medidas del cuero cabelludo, SAINT® usa una resonancia funcional (fMRI) para identificar la subregión específica de la DLPFC con la conexión funcional más fuerte a los circuitos profundos de regulación del ánimo en su cerebro. Esta orientación personalizada es una característica clave de SAINT®. El objetivo es único para cada paciente, elegido a partir de su propia resonancia en lugar de un promedio poblacional.",
  keyTakeaways: [
    "SAINT® estimula la corteza prefrontal dorsolateral izquierda (DLPFC).",
    "El objetivo se personaliza con fMRI, no con medidas del cuero cabelludo.",
    "La fMRI identifica la subregión con la conexión más fuerte a los circuitos del ánimo.",
    "El objetivo de cada paciente es único para su propio cerebro.",
    "Esta orientación personalizada distingue a SAINT® de la TMS convencional.",
  ],
  sections: [
    {
      heading: "La corteza prefrontal dorsolateral izquierda",
      paragraphs: [
        "SAINT® estimula la corteza prefrontal dorsolateral izquierda (DLPFC), una región cerebral implicada en la regulación del ánimo, la función ejecutiva y el control emocional. La DLPFC está poco activa en muchas personas con depresión, y se cree que estimularla con TMS normaliza la actividad en redes más amplias de regulación del ánimo.",
      ],
    },
    {
      heading: "Cómo la fMRI personaliza el objetivo",
      paragraphs: [
        "La TMS convencional orienta la DLPFC con medidas del cuero cabelludo: típicamente se mide desde el punto que hace contraer el pulgar (la ubicación del umbral motor) y se avanza 5.5 cm. Esto es un promedio poblacional, no personalizado. SAINT® reemplaza este enfoque con una resonancia funcional que mapea cómo se conectan funcionalmente distintas regiones cerebrales. Un algoritmo propietario identifica la subregión específica de su DLPFC con la conexión funcional más fuerte a la corteza cingulada subgenual anterior, una región más profunda implicada en la regulación del ánimo.",
      ],
    },
    {
      heading: "Por qué importa la orientación personalizada",
      paragraphs: [
        "El punto 'correcto' para la TMS varía de un cerebro a otro. Dos personas pueden tener sus sitios óptimos de estimulación a centímetros de distancia. Al usar fMRI para encontrar el objetivo individualizado de cada paciente, SAINT® busca estimular la subregión precisa con más probabilidad de afectar los circuitos del ánimo. Esta es una de las innovaciones clave desarrolladas en Stanford.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://pubmed.ncbi.nlm.nih.gov/32552917/",
    },
  ],
  relatedLinks: [
    ...esSaintRelated,
    {
      title: "BEAM F3 vs orientación por fMRI",
      href: "/es/learn/beam-f3-vs-fmri-tms",
      description:
        "Cómo se diferencia la orientación por medida de la cabeza de un objetivo guiado por fMRI",
    },
  ],
  reviewBlock,
} satisfies EsArticleInput);
